<template>
  <li
    class="tw-relative tw-rounded tw-cursor-pointer"
    :class="[groupClass]"
    data-testid="tree-menu-item"
    @click.prevent.stop="onItemClicked($event, item)"
    @mouseenter="onRowMouseEnter"
    @mouseleave="onRowMouseLeave"
  >
    <slot name="accent" :item-for-slot="item">
      <TreeMenuAccent v-if="selected" />
    </slot>
    <div class="tw-relative tw-z-10 tw-grow tw-min-w-0">
      <span
        v-if="hasChildren"
        class="
          sf-tree-menu__group-title
          tw-font-bold tw-rounded tw-text-sf-slate
          hover:tw-text-sf-slate-dark-1 hover:tw-bg-sf-slate-subtle-2
          tw-w-full
        "
        :class="titleClass"
        @click.stop="toggle(item?.id)"
      >
        <i
          v-if="hasChildren"
          class="fa sf-tree-menu__group-title-icon tw-flex-shrink-0"
          :class="[
            iconClass,
            {
              'tw-mr-0.5': variant === 'slim',
              'tw-text-[10px]': variant === 'slim', // design spec: 10px — no standard token
              'tw-w-[10px]': variant === 'slim',
              'tw-mr-1': variant !== 'slim',
            },
          ]"
          :data-testid="`tree-menu-icon-${item?.name || item?.id}`"
          @click.stop="toggle(item?.id)"
        />

        <a
          class="
            sf-tree-menu__item-link
            tw-truncate tw-min-w-0 tw-pointer-events-none
          "
          :class="linkClass"
          :href="ctrlClickPath"
          @click.prevent.stop="onItemClicked($event, item)"
        >
          <span
            v-dompurify-html:noHtml="item.name"
            class="tw-truncate tw-min-w-0"
            :class="labelClass"
          />
          <slot name="itemSlot" :item-for-slot="item" />
        </a>
      </span>

      <a
        v-if="!hasChildren"
        class="
          sf-tree-menu__item-link
          tw-truncate tw-min-w-0 tw-w-full
          hover:tw-text-sf-slate-dark-1
          active:tw-text-sf-slate-dark-1
          focus:tw-text-sf-slate-dark-1
        "
        :class="[{ 'tw-text-sm': variant !== 'slim' }, linkClass]"
        :href="ctrlClickPath"
        @click.prevent.stop="onItemClicked($event, item)"
      >
        <span
          ref="label"
          v-dompurify-html:noHtml="item.name"
          class="tw-grow tw-truncate tw-min-w-0"
          :class="labelClass"
        />
        <slot name="itemSlot" :item-for-slot="item" />
      </a>

      <TreeMenu
        v-if="hasChildren"
        v-show="expanded"
        :level="parentLevel + 1"
        :items="item.children"
        :expanded-items="expandedItems"
        :variant="variant"
        :ctrl-click="ctrlClick"
        :is-dimmed="isDimmed"
        :get-item-data-attrs="getItemDataAttrs"
        @item-expanded="handleItemExpanded"
        @item-highlighted="handleItemHighlighted"
        @item-toggled="toggle"
        @item-clicked="itemClicked"
      >
        <template #accent="{ itemForSlot }">
          <slot name="accent" :item-for-slot="itemForSlot" />
        </template>
        <template #itemSlot="{ itemForSlot }">
          <slot name="itemSlot" :item-for-slot="itemForSlot" />
        </template>
      </TreeMenu>
    </div>
  </li>
</template>

<script>
import { defineAsyncComponent } from 'vue'

import Tooltip from '@app/setup/tooltip/uikit-tooltip'
import TreeMenuAccent from './tree-menu-accent.vue'

export default {
  name: 'TreeMenuItem',
  components: {
    TreeMenuAccent,
    TreeMenu: defineAsyncComponent(() => import('./tree-menu')),
  },
  props: {
    item: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    parentLevel: {
      type: Number,
      required: true,
    },
    expandedItems: {
      type: Object,
      required: true,
    },
    selected: {
      type: Boolean,
      required: false,
    },
    variant: {
      type: String,
      default: 'default',
    },
    ctrlClick: {
      type: Boolean,
      default: true,
    },
    isDimmed: {
      type: Function,
      default: () => () => false,
    },
    getItemDataAttrs: {
      type: Function,
      default: () => () => ({}),
    },
  },
  emits: ['item-clicked', 'item-expanded', 'item-highlighted', 'item-toggled'],
  computed: {
    expanded() {
      return this.expandedItems[this.item.id]
    },
    iconClass() {
      return this.expanded ? 'fa-angle-down' : 'fa-angle-right'
    },
    groupClass() {
      const slim = this.variant === 'slim'
      return {
        'sf-tree-menu__group': this.hasChildren,
        'sf-tree-menu__item': !this.hasChildren,
        'hover:tw-bg-sf-slate-subtle-2': !this.hasChildren,
        'tw-flex': slim && !this.hasChildren,
        'tw-items-center': slim && !this.hasChildren,
        'tw-min-w-0': slim,
        'tw-h-7': slim && !this.hasChildren,
        'tw-py-0': slim && !this.hasChildren,
        'tw-pl-2':
          (this.parentLevel === 1 && !slim) ||
          (slim && this.parentLevel === 1 && !this.hasChildren),
        'tw-pl-3': slim && this.parentLevel > 1 && this.hasChildren,
        'tw-pl-5': slim && this.parentLevel > 1 && !this.hasChildren,
      }
    },
    linkClass() {
      return {
        'tw-text-sf-slate': !this.selected || this.isDimmed(this.item),
        'tw-text-sf-slate-dark-2': this.selected && !this.isDimmed(this.item),
      }
    },
    labelClass() {
      const dimmed = this.isDimmed(this.item)
      return {
        'tw-text-sf-slate-dark-1': dimmed,
        'tw-opacity-40': dimmed,
      }
    },
    titleClass() {
      return {
        'sf-tree-menu__group-title--top-level': this.parentLevel === 1,
        'tw-h-7': this.variant === 'slim',
        'tw-px-2': this.variant === 'slim',
        'tw-py-0': this.variant === 'slim',
        'tw-text-sm': this.variant !== 'slim',
      }
    },
    hasChildren() {
      return this.item.children && this.item.children.length
    },
    ctrlClickPath() {
      return this.ctrlClick ? this.item?.path : '#'
    },
  },
  created() {
    if (this.selected) {
      this.$emit('item-highlighted', this.item.id)
    }
  },
  beforeUnmount() {
    this.onRowMouseLeave()
  },
  methods: {
    expandSelf: function () {
      this.$emit('item-expanded', this.item.id)
    },
    handleItemExpanded: function (itemId) {
      this.$emit('item-expanded', itemId)
    },
    handleItemHighlighted: function (itemId) {
      if (this.hasChildren) {
        this.expandSelf()
      }

      this.$emit('item-highlighted', itemId)
    },
    itemClicked: function (data) {
      this.$emit('item-clicked', data)
    },
    toggle(itemId) {
      this.$emit('item-toggled', itemId)
    },
    onRowMouseEnter() {
      if (this.hasChildren) return
      this.onRowMouseLeave()
      const label = this.$refs.label
      if (!label) return
      const range = document.createRange()
      range.selectNodeContents(label)
      if (range.getBoundingClientRect().width <= label.clientWidth) return
      this._rowTooltip = new Tooltip(this.$el, {
        title: this.item.name,
        placement: 'right',
        trigger: 'manual',
      })
      this._scrollHandler = () => this.onRowMouseLeave()
      document.addEventListener('scroll', this._scrollHandler, true)
      this._rowTooltip.show()
    },
    onRowMouseLeave() {
      if (this._scrollHandler) {
        document.removeEventListener('scroll', this._scrollHandler, true)
        this._scrollHandler = null
      }
      if (this._rowTooltip) {
        this._rowTooltip.dispose()
        this._rowTooltip = null
      }
    },
    onItemClicked(event, item) {
      this.onRowMouseLeave()
      const shouldOpenInNewTab =
        this.ctrlClick && (event.metaKey || event.ctrlKey)

      if (shouldOpenInNewTab) {
        window.open(item.path, '_blank')
        return
      }

      this.$emit('item-clicked', item)
    },
  },
}
</script>
<style scoped>
/*
 * UIkit sets opacity: 0.7 on .sf-tree-menu__item-link:hover/:active/:focus (specificity 0,1,1).
 * UIkit also sets a color on .sf-tree-menu__item-link (specificity 0,1,0) that can beat Tailwind.
 * The scoped [data-v-*] attribute raises our rules to (0,2,1) / (0,3,1) to override both.
 */
.sf-tree-menu__item-link:hover,
.sf-tree-menu__item-link:active,
.sf-tree-menu__item-link:focus {
  opacity: 1;
}

.sf-tree-menu__item-link.tw-text-sf-slate-dark-2 {
  /* sf-slate-dark-2 (#1e293b) — no CSS custom property is available for this token */
  color: #1e293b;
}
</style>
